import { palette } from './palette';
import { aiGradient, themeLight, withOverlay } from './theme.light';
var AI_GRADIENT_DARK = aiGradient(palette.darkAiAccent, palette.darkAiPurple);
/**
 * Dark theme — work in progress. `null` means no dark value has been decided yet; the
 * role falls back to its light value, which is what the whole app does today. Every
 * role must appear here (the `Record<SemanticToken, …>` type enforces it), so adding a
 * role to `theme.light.ts` forces a dark decision — or an explicit `null` — instead of
 * silently inheriting one. Don't spread `themeLight` here: it would make "undecided"
 * and "deliberately same as light" indistinguishable.
 *
 * Dark only renders inside the Teams meeting surface
 * (`helpers/darkThemeHelpers.ts#checkIfMeetingAndDarkTheme`) — the main tab app is
 * light-only.
 */ var DARK_VALUES = {
    // ---- group 1: direct provenance -------------------------------------
    // `oneOnOneThemeHelper.ts#getThemeVariables`'s dark branch already answers these.
    // Fill from: primaryTalkingPointColor → white, secondaryColor → #c8c6c4,
    // blueWhiteColor → white (both brand roles below).
    colorNeutralForeground1: palette.white,
    colorNeutralForeground1Body: palette.white,
    colorNeutralForeground2: palette.textDisabledGray,
    colorNeutralForeground3: null,
    colorNeutralForegroundMuted: null,
    colorBrandForeground1: palette.white,
    colorBrandStroke1: palette.white,
    // ---- group 2: one decision ------------------------------------------
    // Two candidates: #2D2C2C (control surface) or #282727 (MeetingIntelligenceBox).
    // Picked #282727: it is what every dark surface in the app already paints — the MI box and the
    // transcript/recap modals that ride on its palette — so the token describes reality today.
    colorNeutralBackground1: palette.darkSurface,
    // ---- group 3: genuinely new -----------------------------------------
    // No dark counterpart exists anywhere in the app. Needs design input, not derivation.
    colorNeutralForegroundDisabled: null,
    colorNeutralForeground1Disabled: null,
    colorBrandBackground: null,
    colorBrandBackgroundHover: null,
    colorBrandBackgroundPressed: null,
    colorNeutralForegroundOnBrand: null,
    colorNeutralBackground1Hover: palette.darkSurfaceHover,
    colorNeutralBackground1Pressed: palette.darkSurfacePressed,
    colorNeutralBackground1Disabled: null,
    // The transcript modal's segment divider, previously a hand-picked #484644 in its own `.dark`
    // rule; `darkBorder` is the same quiet line against `darkSurface`, two digits apart.
    colorNeutralStroke1: palette.darkBorder,
    colorNeutralStroke1Disabled: null,
    colorTransparentBackgroundHover: palette.darkTransparentHover,
    colorTransparentBackgroundPressed: palette.darkTransparentPressed,
    colorPaletteRedForeground1: palette.darkDangerRed,
    colorPaletteRedForegroundText: null,
    colorPaletteGreenForegroundText: null,
    colorPaletteYellowForegroundText: null,
    disabledOpacity: null,
    // ---- group 4: notice surfaces ---------------------------------------
    // The warning notice (`.mi-hint--warning`) is a neutral grey block with white text and icon on
    // dark, per design — the light amber tint reads wrong on #282727.
    colorPaletteYellowBackground1: palette.darkNoticeSurface,
    colorPaletteYellowForeground1: palette.white,
    colorPaletteYellowForeground2: palette.white,
    // Paired with colorBrandForeground1 (white in dark): `.mi-hint--brand` and `.mi-empty-state-tile`
    // put that foreground on this fill, so the light #ecedf4 would leave white text unreadable.
    colorBrandBackground2: palette.darkSurfaceHover,
    colorBrandAccentForeground: palette.darkAiAccent,
    // ---- ai ------------------------------------------------------------------
    // Design's dark gradient (Figma) — the light one's dark ends sink into `darkSurface`.
    // The subtle (pastel) pair has no dark spec yet.
    colorAiBackground: AI_GRADIENT_DARK,
    colorAiBackgroundHover: withOverlay(0.08, AI_GRADIENT_DARK),
    colorAiBackgroundPressed: withOverlay(0.15, AI_GRADIENT_DARK),
    colorAiSubtleBackground: null,
    colorAiSubtleBackgroundHover: null,
    colorAiStroke: AI_GRADIENT_DARK,
    colorAiForeground: AI_GRADIENT_DARK
};
/** Roles still on their light value — remaining work, in one greppable place. */ export var UNDECIDED_DARK_ROLES = Object.keys(DARK_VALUES).filter(function(role) {
    return DARK_VALUES[role] === null;
});
export var themeDark = Object.fromEntries(Object.keys(themeLight).map(function(role) {
    var _DARK_VALUES_role;
    return [
        role,
        (_DARK_VALUES_role = DARK_VALUES[role]) !== null && _DARK_VALUES_role !== void 0 ? _DARK_VALUES_role : themeLight[role]
    ];
}));
